import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Download, Eye, Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import { generateColorVariant, getKitById } from '../api/kit.api';
import EnhancedImagePreview from './EnhancedImagePreview';

const ColorVariantsModal = ({ isOpen, onClose, kitId, showNotification, refreshKits, fabricColorVariants }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showWatermarked, setShowWatermarked] = useState(false);
  const [isGeneratingColorVariant, setIsGeneratingColorVariant] = useState(false);
  const [generatingColorHex, setGeneratingColorHex] = useState(null);
  const [updatedKit, setUpdatedKit] = useState(null);
  const [isLoadingKitData, setIsLoadingKitData] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [failedThumbs, setFailedThumbs] = useState({});
  // Ensure the modal only closes via explicit user action
  const [isVisible, setIsVisible] = useState(false);
  
  // Enhanced image preview state
  const [showEnhancedPreview, setShowEnhancedPreview] = useState(false);
  const [enhancedPreviewImage, setEnhancedPreviewImage] = useState(null);
  const [enhancedPreviewTitle, setEnhancedPreviewTitle] = useState('');

  // Track visibility: once opened, keep visible until explicit close
  useEffect(() => {
    console.log('🎨 ColorVariantsModal - isOpen changed:', isOpen, 'kitId:', kitId);
    if (isOpen) {
      console.log('🎨 ColorVariantsModal - Setting isVisible to true');
      setIsVisible(true);
    }
  }, [isOpen, kitId]);

  // Fetch latest kit data when modal opens
  useEffect(() => {
    const fetchLatestKitData = async () => {
      if (isOpen && kitId) {
        setIsLoadingKitData(true);
        setApiError(null);
        
        // Add timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          console.log('⏰ API call timeout - setting loading to false');
          setIsLoadingKitData(false);
          setApiError('Request timeout - please try again');
        }, 10000); // 10 second timeout
        
        try {
          console.log('🔄 Fetching latest kit data for modal with kitId:', kitId);
          console.log('🔄 API call starting...');
          const response = await getKitById(kitId);
          console.log('🔄 API call completed, response:', response);
          
          // Clear timeout since API call completed
          clearTimeout(timeoutId);
          
          if (response && (response.success || response._id || response.name)) {
            console.log('✅ Latest kit data fetched successfully');
            console.log('🎨 Full API response:', response);
            
            // Handle different response structures
            let kitData = null;
            if (response.success && response.data?.kit) {
              // Structure: { success: true, data: { kit: {...} } }
              kitData = response.data.kit;
              console.log('🎨 Using response.data.kit structure');
            } else if (response._id || response.name) {
              // Structure: Direct kit object
              kitData = response;
              console.log('🎨 Using direct response structure');
            }
            
            console.log('🎨 Kit data to use:', kitData);
            console.log('🎨 Generated color variants in fetched data:', kitData?.generatedColorVariants);
            console.log('🎨 Color variants in fetched data:', kitData?.colorVariants);
            console.log('🎨 Generated image URL:', kitData?.generatedImageUrl);
            setUpdatedKit(kitData);
            console.log('🎨 Set updatedKit to:', kitData);
          } else {
            console.error('❌ Failed to fetch latest kit data:', response?.message || 'Unknown error');
            console.error('❌ Full response:', response);
            setUpdatedKit(null);
          }
        } catch (error) {
          console.error('❌ Error fetching latest kit data:', error);
          console.error('❌ Error details:', error.message, error.stack);
          setUpdatedKit(null);
          setApiError(`Failed to load kit data: ${error.message}`);
        } finally {
          console.log('🔄 Setting isLoadingKitData to false');
          console.log('🔄 Current state - isLoadingKitData will be set to false');
          clearTimeout(timeoutId);
          setIsLoadingKitData(false);
          
          // Force loading state to false after a short delay to test
          setTimeout(() => {
            console.log('🔄 FORCE: Setting isLoadingKitData to false after timeout');
            setIsLoadingKitData(false);
          }, 1000);
        }
      } else {
        setUpdatedKit(null);
        setIsLoadingKitData(false);
      }
      setCurrentImageIndex(0); // Reset to first image when kit changes
    };

    fetchLatestKitData();
  }, [isOpen, kitId]);

  // Get all color variant images from the kit, including all colors from the palette
  const getAllColorVariantImages = () => {
    const images = [];
    const currentKit = updatedKit;
    
    console.log('🎨 Current kit data for color variants:', currentKit);
    console.log('🎨 Using updatedKit:', !!updatedKit);
    console.log('🎨 isLoadingKitData:', isLoadingKitData);
    
    // Return empty array if no kit data is available yet
    if (!currentKit) {
      console.log('🎨 No kit data available yet');
      return [];
    }
    
    // Add the main generated image
    if (currentKit.generatedImageUrl) {
      images.push({
        url: currentKit.generatedImageUrl,
        watermarkedUrl: currentKit.watermarkedImages?.generatedImageUrl || currentKit.watermarkedImageUrl,
        type: 'main',
        label: 'Original Generated',
        color: null,
        hex: null
      });
    }

    // Get all colors from the kit's color palette
    // Prefer fabric color variants when passed (from fabric detail page); otherwise use kit's
    const allColors = [];
    const sourceColorVariants = (fabricColorVariants && fabricColorVariants.length > 0)
      ? fabricColorVariants
      : (currentKit.colorVariants || []);

    if (sourceColorVariants.length > 0) {
      sourceColorVariants.forEach((variant, index) => {
        allColors.push({
          hex: variant.hex,
          colorName: variant.colorName || variant.name || `Color ${index + 1}`,
          hasGeneratedImage: false
        });
      });
    }

    // Add colors from generatedColorVariants (already generated)
    if (currentKit.generatedColorVariants && currentKit.generatedColorVariants.length > 0) {
      console.log('🎨 Found generatedColorVariants:', currentKit.generatedColorVariants);
      currentKit.generatedColorVariants.forEach((variant) => {
        console.log('🎨 Processing generated variant:', variant);
        console.log('🎨 Looking for existing color with hex:', variant.hex);
        console.log('🎨 Current allColors:', allColors.map(c => ({ hex: c.hex, colorName: c.colorName })));
        
        // Check if this color already exists in allColors (case-insensitive hex comparison)
        const existingColorIndex = allColors.findIndex(color => {
          const match = color.hex && variant.hex && color.hex.toLowerCase() === variant.hex.toLowerCase();
          console.log(`🎨 Comparing ${color.hex} with ${variant.hex}: ${match}`);
          return match;
        });
        if (existingColorIndex >= 0) {
          // Update existing color with generated image info
          console.log(`🎨 Found existing color at index ${existingColorIndex}, updating with generated image`);
          allColors[existingColorIndex].hasGeneratedImage = true;
          allColors[existingColorIndex].generatedImageUrl = variant.generatedImageUrl;
          allColors[existingColorIndex].colorName = variant.colorName || allColors[existingColorIndex].colorName;
          console.log('🎨 Updated existing color:', allColors[existingColorIndex]);
        } else {
          // Add new color
          console.log(`🎨 No existing color found for ${variant.hex}, adding as new color`);
          const newColor = {
            hex: variant.hex,
            colorName: variant.colorName || `Generated Color`,
            hasGeneratedImage: true,
            generatedImageUrl: variant.generatedImageUrl
          };
          allColors.push(newColor);
          console.log('🎨 Added new generated color:', newColor);
        }
      });
    } else {
      console.log('🎨 No generatedColorVariants found in current kit');
    }

    console.log('🎨 All colors processed:', allColors);

    // Create images for all colors
    allColors.forEach((color) => {
      const imageData = {
        url: color.hasGeneratedImage ? color.generatedImageUrl : null,
        watermarkedUrl: null,
        type: 'variant',
        label: color.colorName,
        color: color.hex,
        hex: color.hex,
        hasGeneratedImage: color.hasGeneratedImage,
        isGenerating: isGeneratingColorVariant && generatingColorHex === color.hex
      };
      images.push(imageData);
      console.log('🎨 Created image for color:', color.colorName, 'hasGeneratedImage:', color.hasGeneratedImage, 'url:', imageData.url);
    });

    console.log('🎨 Final images array:', images);
    return images;
  };

  const allImages = getAllColorVariantImages();
  const currentImage = allImages[currentImageIndex];

  const getDisplayImage = () => {
    if (showWatermarked && currentImage.watermarkedUrl) {
      return currentImage.watermarkedUrl;
    }
    return currentImage.url;
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const downloadImage = () => {
    const link = document.createElement('a');
    link.href = getDisplayImage();
    const kitName = updatedKit?.name || 'kit';
    link.download = `kit-color-variant-${kitName}-${currentImageIndex + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Enhanced image preview functions
  const openEnhancedPreview = (imageUrl, title) => {
    setEnhancedPreviewImage(imageUrl);
    setEnhancedPreviewTitle(title);
    setShowEnhancedPreview(true);
  };

  const closeEnhancedPreview = () => {
    setShowEnhancedPreview(false);
    setEnhancedPreviewImage(null);
    setEnhancedPreviewTitle('');
  };

  const handleEnhancedImageChange = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  const handleEnhancedDownload = (imageUrl, title) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    const kitName = updatedKit?.name || 'kit';
    link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if any watermarked images exist
  const hasWatermarkedImages = allImages.some(img => img.watermarkedUrl);

  // Handle generating color variant for missing colors
  const handleGenerateColorVariant = async (colorHex) => {
    if (!colorHex || isGeneratingColorVariant) return;

    setIsGeneratingColorVariant(true);
    setGeneratingColorHex(colorHex);

    try {
      console.log('🎨 Generating color variant for:', colorHex);
      const response = await generateColorVariant(kitId, colorHex);
      
      if (response.success) {
        // showNotification(`Color variant generated successfully!`, "success");
        
        // Refetch only the specific kit data to get the latest color variants
        console.log('🔄 Refetching kit data after color variant generation...');
        try {
          const updatedKitResponse = await getKitById(kitId);
          if (updatedKitResponse && (updatedKitResponse.success || updatedKitResponse._id || updatedKitResponse.name)) {
            let kitData = null;
            if (updatedKitResponse.success && updatedKitResponse.data?.kit) {
              kitData = updatedKitResponse.data.kit;
            } else if (updatedKitResponse._id || updatedKitResponse.name) {
              kitData = updatedKitResponse;
            }
            console.log('✅ Kit data refetched successfully:', kitData);
            setUpdatedKit(kitData);
          } else {
            console.log('⚠️ Using response data as fallback');
            setUpdatedKit(response.data);
          }
        } catch (refetchError) {
          console.error('❌ Failed to refetch kit data:', refetchError);
          console.log('⚠️ Using response data as fallback');
          setUpdatedKit(response.data);
        }
      } else {
        showNotification(`Failed to generate color variant: ${response.message}`, "error");
      }
    } catch (error) {
      console.error('Error generating color variant:', error);
      showNotification(`Error generating color variant: ${error.message}`, "error");
    } finally {
      setIsGeneratingColorVariant(false);
      setGeneratingColorHex(null);
    }
  };

  if (!isVisible || !kitId) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col h-screen bg-gray-50 overflow-hidden">
      {/* Header with Back Button */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0 sticky top-0 z-10 shadow-sm pt-safe-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <button
                onClick={() => {
                  setIsVisible(false);
                  if (typeof onClose === 'function') onClose();
                }}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition hover:bg-gray-100 active:scale-95"
                aria-label="Go back"
              >
                <ArrowLeft className="h-6 w-6 text-gray-700" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Color Variants</h1>
                  <p className="text-sm text-gray-600">{updatedKit?.name || 'Loading...'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {(() => {
            console.log('🎨 RENDERING DEBUG:');
            console.log('🎨 isLoadingKitData:', isLoadingKitData);
            console.log('🎨 updatedKit:', !!updatedKit);
            console.log('🎨 apiError:', apiError);
            console.log('🎨 allImages.length:', allImages.length);
            return null;
          })()}
          {isLoadingKitData ? (
            <div className="flex flex-col items-center justify-center text-center py-12">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-lg font-semibold text-gray-700 mb-2">Loading Latest Data</p>
              <p className="text-sm text-gray-600">Fetching the most up-to-date kit information...</p>
            </div>
          ) : apiError ? (
            <div className="flex flex-col items-center justify-center text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-2">Error Loading Data</p>
              <p className="text-sm text-gray-600 mb-4">{apiError}</p>
              <button
                onClick={() => {
                  setApiError(null);
                  setIsLoadingKitData(true);
                  // Trigger re-fetch
                  const fetchLatestKitData = async () => {
                    try {
                      const response = await getKitById(kitId);
                      if (response && (response.success || response._id || response.name)) {
                        let kitData = null;
                        if (response.success && response.data?.kit) {
                          kitData = response.data.kit;
                        } else if (response._id || response.name) {
                          kitData = response;
                        }
                        setUpdatedKit(kitData);
                        setApiError(null);
                      } else {
                        setApiError('Failed to load kit data');
                      }
                    } catch (error) {
                      setApiError(`Failed to load kit data: ${error.message}`);
                    } finally {
                      setIsLoadingKitData(false);
                    }
                  };
                  fetchLatestKitData();
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : !updatedKit ? (
            <div className="flex flex-col items-center justify-center text-center py-12">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-lg font-semibold text-gray-700 mb-2">Loading Kit Data</p>
              <p className="text-sm text-gray-600">Fetching kit information from the server...</p>
            </div>
          ) : allImages.length > 0 ? (
            <div className="space-y-6">
              {/* Main Image Display */}
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div className="aspect-[4/3] flex items-center justify-center relative bg-gradient-to-br from-gray-50 to-gray-100">
                  {currentImage.url && !currentImage.isGenerating ? (
                    <img
                      src={getDisplayImage()}
                      alt={`${updatedKit?.name || 'Kit'} - ${currentImage.label}`}
                      className="max-w-full max-h-full object-contain rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-200"
                      onClick={() => openEnhancedPreview(getDisplayImage(), `${updatedKit?.name || 'Kit'} - ${currentImage.label}`)}
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/600x400/E0E7FF/4F46E5?text=Image+Not+Available';
                      }}
                    />
                  ) : currentImage.isGenerating ? (
                    <div className="flex flex-col items-center justify-center text-center p-8">
                      <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                      <p className="text-lg font-semibold text-gray-700 mb-2">Generating Color Variant</p>
                      <p className="text-sm text-gray-600">Please wait while we generate the image for {currentImage.hex}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center p-8">
                      <div className="w-20 h-20 rounded-full border-4 border-gray-300 mb-4 flex items-center justify-center">
                        <div 
                          className="w-16 h-16 rounded-full"
                          style={{ backgroundColor: currentImage.hex || '#f3f4f6' }}
                        ></div>
                      </div>
                      <p className="text-lg font-semibold text-gray-700 mb-2">{currentImage.label}</p>
                      <p className="text-sm text-gray-600 mb-4">Color variant not generated yet</p>
                      <button
                        onClick={() => handleGenerateColorVariant(currentImage.hex)}
                        disabled={isGeneratingColorVariant}
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span>Generate Color Variant</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Navigation Arrows */}
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white text-gray-700 rounded-xl flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl backdrop-blur-sm"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white text-gray-700 rounded-xl flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl backdrop-blur-sm"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  {allImages.length > 1 && (
                    <div className="absolute top-4 right-4 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-xl text-sm font-semibold text-gray-700 shadow-lg">
                      {currentImageIndex + 1} / {allImages.length}
                    </div>
                  )}

                  {/* Image Type Badge */}
                  <div className="absolute top-4 left-4">
                    <div className={`px-3 py-2 rounded-xl text-sm font-semibold shadow-lg backdrop-blur-sm ${
                      currentImage.type === 'main' 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                        : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white'
                    }`}>
                      {currentImage.type === 'main' ? 'Main Image' : 'Color Variant'}
                    </div>
                  </div>

                  {/* Watermark Toggle */}
                  {hasWatermarkedImages && (
                    <div className="absolute bottom-4 right-4">
                      <button
                        onClick={() => setShowWatermarked(!showWatermarked)}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg backdrop-blur-sm ${
                          showWatermarked 
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white' 
                            : 'bg-white/90 text-gray-700 hover:bg-white hover:shadow-xl border border-gray-200'
                        }`}
                      >
                        {showWatermarked ? 'Watermarked' : 'Original'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Image Info */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{currentImage.label}</h3>
                    {currentImage.color && (
                      <div className="flex items-center space-x-2 mt-2">
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-gray-300 shadow-sm"
                          style={{ backgroundColor: currentImage.color }}
                        ></div>
                        <span className="text-sm font-medium text-gray-600">{currentImage.color}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 flex-wrap">
                    <button
                      onClick={() => openEnhancedPreview(getDisplayImage(), `${updatedKit?.name || 'Kit'} - ${currentImage.label}`)}
                      className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">Full Screen</span>
                      <span className="sm:hidden">View</span>
                    </button>
                    
                    <button
                      onClick={downloadImage}
                      className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Color Palette section removed as per requirement */}

              {/* Thumbnail Navigation */}
              {allImages.length > 1 && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Image Previews</h4>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                    {allImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (image.url) {
                            setCurrentImageIndex(index);
                          } else if (image.color && !isGeneratingColorVariant) {
                            handleGenerateColorVariant(image.hex || image.color);
                          }
                        }}
                        disabled={isGeneratingColorVariant && generatingColorHex === (image.hex || image.color)}
                        className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                          index === currentImageIndex
                            ? 'border-blue-500 shadow-lg scale-105'
                            : 'border-gray-200 hover:border-blue-300 hover:scale-105'
                        } ${isGeneratingColorVariant && generatingColorHex === (image.hex || image.color) ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {image.url && !failedThumbs[index] ? (
                          <img
                            src={image.url}
                            alt={image.label}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setFailedThumbs((prev) => ({ ...prev, [index]: true }));
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: image.color || '#eef2ff' }}>
                            <span className="text-[10px] sm:text-xs font-medium text-gray-700 bg-white/80 px-2 py-0.5 rounded">
                              {image.color ? (isGeneratingColorVariant && generatingColorHex === (image.hex || image.color) ? 'Generating…' : 'Generate') : 'No Image'}
                            </span>
                          </div>
                        )}
                        {isGeneratingColorVariant && generatingColorHex === (image.hex || image.color) && (
                          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-gray-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Color Variants Found</h3>
              <p className="text-gray-600 mb-4">This kit doesn't have any color variants yet.</p>
              <p className="text-sm text-gray-500">Kit data: {updatedKit ? 'Loaded' : 'Not loaded'}</p>
              <p className="text-sm text-gray-500">Images count: {allImages.length}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Enhanced Image Preview */}
      <EnhancedImagePreview
        isOpen={showEnhancedPreview}
        onClose={closeEnhancedPreview}
        imageUrl={enhancedPreviewImage}
        title={enhancedPreviewTitle}
        images={allImages.map(img => ({ url: img.url, title: img.label }))}
        currentIndex={currentImageIndex}
        onImageChange={handleEnhancedImageChange}
        showMetadata={true}
        allowDownload={true}
        allowShare={true}
        allowEdit={false}
        onDownload={handleEnhancedDownload}
        onShare={async (url, title) => {
          try {
            if (navigator.share) {
              await navigator.share({
                title: title,
                text: `Check out this kit image: ${title}`,
                url: url
              });
              if (showNotification) {
                showNotification('Image shared successfully!', 'success');
              }
            } else {
              // Fallback to clipboard
              await navigator.clipboard.writeText(url);
              if (showNotification) {
                showNotification('Image URL copied to clipboard!', 'success');
              }
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              // User cancelled share - this is normal, don't show error
              console.log('Share cancelled by user');
            } else {
              console.error('Share failed:', error);
              // Fallback to clipboard
              try {
                await navigator.clipboard.writeText(url);
                if (showNotification) {
                  showNotification('Image URL copied to clipboard!', 'success');
                }
              } catch (clipboardError) {
                if (showNotification) {
                  showNotification('Unable to share or copy image URL', 'error');
                }
              }
            }
          }
        }}
      />
    </div>
  );
};

export default ColorVariantsModal;
